import {Component, Input, OnInit, Type, ViewChild, ViewContainerRef} from '@angular/core';
import {InspectableItemType, InspectorEvent, InspectorImplementation, WorkbenchContext, WorkbenchViewVisibilityKind} from '../../types';
import {faHandPointer} from '@fortawesome/pro-solid-svg-icons';
import {AuditInspectorComponent} from './audit-inspector/audit-inspector.component';
import {InspectorService} from '../../services/inspector.service';
import {ScreenshotInspectorComponent} from './screenshot-inspector/screenshot-inspector.component';
import {WorkbenchToolService} from '../../services/workbench-tool.service';
import {WorkbenchLayoutManager} from '../../services/workbench-layout-manager.service';
import {InspectorPlugin} from '../../../workbench-plugins/inspector/inspector-plugin';
import {TestmailInspectorComponent} from './testmail-inspector/testmail-inspector.component';
import {VideoRecordingInspectorComponent} from './video-recording-inspector/video-recording-inspector.component';
import {DeviceDetailsInspectorComponent} from './devicedetails-inspector/devicedetails-inspector.component';
import {WorkbenchCommandRegistry} from '../../services/workbench-command-registry.service';
import {WorkbenchContextService} from '../../services/workbench-context.service';
import {WorkbenchModeVisibilityService} from '../../services/workbench-mode-visibility.service';

type InspectorDefinition = {
    component: Type<InspectorImplementation>;
    title: string;
    visibility: WorkbenchViewVisibilityKind;
};

@Component({
    // No Selector because styles otherwise don't work - selector: 'wm-inspector',
    templateUrl: './inspector.component.html',
    styleUrls: ['./inspector.component.less']
})
export class InspectorComponent implements OnInit {

    protected readonly faHandPointer = faHandPointer;

    // Using a Record will show an error if a new InspectableItemType is created without updating this Record
    private readonly availableInspectors: Record<InspectableItemType, InspectorDefinition> = {
        'Audit': {
            component: AuditInspectorComponent,
            title: 'Audit Task',
            visibility: 'audit'
        },
        'Screenshot': {
            component: ScreenshotInspectorComponent,
            title: 'Screenshot',
            visibility: 'generic'
        },
        'VideoRecording': {
            component: VideoRecordingInspectorComponent,
            title: 'Video Recording',
            visibility: 'generic'
        },
        'TestMail': {
            component: TestmailInspectorComponent,
            title: 'Test Mail',
            visibility: 'generic'
        },
        'DeviceDetails': {
            component: DeviceDetailsInspectorComponent,
            title: 'Device Details',
            visibility: 'generic'
        },
        'Default': {
            component: InspectorComponent,
            title: 'Default',
            visibility: 'generic'
        },
    }

    @Input() item!: InspectableItemType;
    title: string;

    @ViewChild('inspectorContainer', { read: ViewContainerRef, static: true })
    inspectorContainer?: ViewContainerRef;

    currentInspector?: InspectorImplementation;
    private currentContext?: WorkbenchContext;

    constructor(private readonly inspectorService: InspectorService,
                private readonly toolsService: WorkbenchToolService,
                private readonly workbenchLayoutManager: WorkbenchLayoutManager,
                private readonly workbenchCommandRegistry: WorkbenchCommandRegistry,
                private readonly workbenchContextService: WorkbenchContextService,
                private readonly modeVisibilityService: WorkbenchModeVisibilityService) {
        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => {
            const previousMode = this.currentContext?.activeMode.kind;
            this.currentContext = context;
            if (previousMode && previousMode !== context.activeMode.kind && this.item && !this.isInspectorVisible(this.item)) {
                this.closeInspector();
            }
        });

        this.inspectorService.getInspectorEvent$().subscribe( event => {
            this.handleInspectorEvent(event);
        })
    }

    ngOnInit(): void {
        // Subscribe here (not in the constructor) so the static `inspectorContainer` ViewChild is already
        // resolved. This matters when an inspector item is set before this view is created (e.g. an audit
        // activity restored on workbench open): the BehaviorSubject replays that item synchronously, and
        // refreshInspectorComponent() needs the container to render the inspector.
        this.inspectorService.getInspectorItem$().subscribe(item => {
            if (!!item && this.item !== item) {
                this.item = item;
                if (this.isInspectorVisible(item)) {
                    this.refreshInspectorComponent();
                } else {
                    this.closeInspector();
                }
            }
        });
    }

    closeInspector() {
        this.inspectorService.changeItem(null);
        this.inspectorContainer?.clear();
        this.currentInspector = undefined;
        this.title = "No element selected";
        this.workbenchLayoutManager.changeViewName(InspectorPlugin.InspectorViewId, this.title);
        this.toolsService.clearRelatedTools();
        this.item = "Default";
    }

    handleInspectorEvent(event: InspectorEvent){
        if (event.type === 'ClearInspector') {
            this.closeInspector();
        } else {
            if (!this.item || this.item === 'Default' || !this.isInspectorVisible(this.item) || !this.currentInspector) {
                return;
            }
            if (this.availableInspectors[this.item].title != this.title) {
                this.refreshInspectorComponent();
            }
            this.currentInspector.handleInspectorEvent(event);
        }
    }

    refreshInspectorComponent() {
        if (!this.inspectorContainer) {
            return;
        }
        if (!this.item || !this.isInspectorVisible(this.item)) {
            this.closeInspector();
            return;
        }
        this.toolsService.clearRelatedTools();
        this.inspectorContainer?.clear();

        let component: Type<InspectorImplementation>;
        component = this.availableInspectors[this.item].component;
        this.title = this.availableInspectors[this.item].title;

        if (component) {
            this.currentInspector = this.inspectorContainer.createComponent(component).instance;
            this.workbenchLayoutManager.changeViewName(InspectorPlugin.InspectorViewId, this.title);
        }
    }

    private isInspectorVisible(item: InspectableItemType): boolean {
        return this.modeVisibilityService.isVisibilityKindVisible(this.availableInspectors[item].visibility, this.currentContext);
    }
}
